import { ISlotPattern, TWeekday } from "../../../entities/schedule";
import { SCHEDULE_SLOT_COUNT, SCHEDULE_SLOT_DURATION_MINUTES, SCHEDULE_WEEKDAYS } from "./constants";

export type TScheduleSlotStatus = 'disabled' | 'available' | 'selected' | 'conflict'

export interface IScheduleSlotCell {
    weekday: TWeekday;
    slotIndex: number;
}

export const getSlotTimeLabel = (slotIndex: number): string => {
    const minutes = slotIndex * SCHEDULE_SLOT_DURATION_MINUTES
    const hours = Math.floor(minutes / 60)
    const restMinutes = minutes % 60

    return `${String(hours).padStart(2, '0')}:${String(restMinutes).padStart(2, '0')}`
}

export const isWeekday = (value: string): value is TWeekday => {
    return SCHEDULE_WEEKDAYS.some(weekday => weekday.value === value)
}

export const getWeekdayLabel = (weekday: TWeekday): string => {
    return SCHEDULE_WEEKDAYS.find(item => item.value === weekday)?.label ?? weekday
}

export const getWeekdayShortLabel = (weekday: TWeekday): string => {
    return SCHEDULE_WEEKDAYS.find(item => item.value === weekday)?.shortLabel ?? weekday
}

export const isSlotInPattern = (pattern: ISlotPattern, cell: IScheduleSlotCell): boolean => {
    return pattern.weekday === cell.weekday &&
        cell.slotIndex >= pattern.startSlot &&
        cell.slotIndex < pattern.startSlot + pattern.durationSlots
}

export const isSlotInPatterns = (patterns: ISlotPattern[], cell: IScheduleSlotCell): boolean => {
    return patterns.some(pattern => isSlotInPattern(pattern, cell))
}

export const getPatternKey = (pattern: ISlotPattern): string => {
    return `${pattern.weekday}-${pattern.startSlot}-${pattern.durationSlots}`
}

export const getCellKey = (cell: IScheduleSlotCell): string => {
    return `${cell.weekday}-${cell.slotIndex}`
}

export const getCellsFromPatterns = (patterns: ISlotPattern[]): Set<string> => {
    return patterns.reduce((acc, pattern) => {
        for(
            let slotIndex = pattern.startSlot;
            slotIndex < Math.min(pattern.startSlot + pattern.durationSlots, SCHEDULE_SLOT_COUNT);
            slotIndex++
        ){
            acc.add(getCellKey({weekday: pattern.weekday, slotIndex}))
        }

        return acc
    }, new Set<string>())
}

export const getPatternsFromCells = (cells: IScheduleSlotCell[]): ISlotPattern[] => {
    const result: ISlotPattern[] = []

    SCHEDULE_WEEKDAYS.forEach(({value}) => {
        const slots = cells
            .filter(cell => cell.weekday === value)
            .map(cell => cell.slotIndex)
            .sort((a, b) => a - b)

        let rangeStart: number | null = null
        let previousSlot: number | null = null

        slots.forEach(slotIndex => {
            if(rangeStart === null){
                rangeStart = slotIndex
                previousSlot = slotIndex
                return
            }

            if(previousSlot !== null && slotIndex === previousSlot + 1){
                previousSlot = slotIndex
                return
            }

            result.push({
                weekday: value,
                startSlot: rangeStart,
                durationSlots: (previousSlot ?? rangeStart) - rangeStart + 1,
            })

            rangeStart = slotIndex
            previousSlot = slotIndex
        })

        if(rangeStart !== null){
            result.push({
                weekday: value,
                startSlot: rangeStart,
                durationSlots: (previousSlot ?? rangeStart) - rangeStart + 1,
            })
        }
    })

    return result
}

export const getScheduleSlotCells = (): IScheduleSlotCell[] => {
    return SCHEDULE_WEEKDAYS.flatMap(({value}) => (
        Array.from({length: SCHEDULE_SLOT_COUNT}, (_, slotIndex) => ({
            weekday: value,
            slotIndex,
        }))
    ))
}
